import { POST } from '@/api'
import { cloneDeep, isEmpty, pick } from 'lodash'
import { differenceInCalendarDays } from 'date-fns'
import { formatDate, today } from '@/utils/date'
import { padZero } from '@/utils/number'
import { syncFromGnavi } from '@/resources/reservation/syncTypeMap'

/**
 * テーブル一覧の共通module
 * - SSに送信する必要があるstateをまとめている
 * - SSに送信する必要がないstateは各moduleを利用する
 */

const DEFAULT_CUSTOMER = {
  id: '',
  lastNameKana: '',
  firstNameKana: '',
  lastName: '',
  firstName: '',
  tel: '',
  tel2: '',
  officeName: '',
  mail1: '',
  mail2: '',
}

export const baseState = {
  originCustomer: { ...DEFAULT_CUSTOMER },
  customer: { ...DEFAULT_CUSTOMER },
  reservation: {
    id: null,
    table: '',
    courses: [],
    reserveDate: {
      year: '',
      month: '',
      day: '',
      weekday: '',
      hour: '',
      minute: '',
    },
    utilizationTime: 0,
    adults: 0,
    children: 0,
    resetTime: null,
    type: 1,
    media: 0,
    purpose: 0,
    settlement: 0,
    syncType: 0,
    freehandMemo: '',
    remarks: '',
    tags: [],
    reserveName: '',
  },
  staffId: '',
  duplicatedIds: '',
  busy: false,
}

export const baseMutations = {
  updateReservation(state, value) {
    state.reservation = { ...state.reservation, ...value }
  },
  updateReservationId(state, value) {
    state.reservation.id = value
  },
  updateReservationYear(state, value) {
    state.reservation.reserveDate.year = value
  },
  updateReservationMonth(state, value) {
    state.reservation.reserveDate.month = value
  },
  updateReservationDay(state, value) {
    state.reservation.reserveDate.day = value
  },
  updateReservationWeekday(state, value) {
    state.reservation.reserveDate.weekday = value
  },
  updateReservationHour(state, value) {
    state.reservation.reserveDate.hour = value
  },
  updateReservationMinute(state, value) {
    state.reservation.reserveDate.minute = value
  },
  /* step2 */
  updateReservationTable(state, value) {
    state.reservation.table = value
  },
  updateReservationUtilizationTime(state, value) {
    state.reservation.utilizationTime = value
  },
  updateDuplicatedIds(state, value) {
    state.duplicatedIds = value
  },
  /* step3 */
  updateCustomer(state, value) {
    state.customer = { ...state.customer, ...value }
  },
  // 選択済みのコース
  updateReservationCourse(state, value) {
    state.reservation.courses = value
  },
  updateReservationSettlement(state, value) {
    state.reservation.settlement = value
  },
  updateStaffId(state, value) {
    state.staffId = value
  },
  busy(state, value) {
    state.busy = value
  },
}

export const baseGetters = {
  /**
   * ネット予約連携中か判定
   */
  isReservedByGnavi(state) {
    return !!syncFromGnavi[state.reservation.syncType]
  },
  /**
   * ネット予約連携中で予約日が本日から90日を超えるか判定
   */
  isOverDayReservedByGnavi(state, getters) {
    if (!state.reservation.id || !getters.isReservedByGnavi) return false
    const { year, month, day } = state.reservation.reserveDate
    return differenceInCalendarDays(`${year}-${month}-${day}`, today()) >= 90
  },
}

export const baseActions = {
  updateReservation({ commit }, reservation) {
    commit('updateReservation', reservation)
  },
  updateReservationId({ commit }, value) {
    commit('updateReservationId', parseInt(value, 10))
  },
  updateReservationDate({ commit }, value) {
    commit('updateReservationYear', formatDate(value, 'YYYY'))
    commit('updateReservationMonth', formatDate(value, 'MM'))
    commit('updateReservationDay', formatDate(value, 'DD'))
    commit('updateReservationWeekday', formatDate(value, 'dd'))
  },
  updateReservationHour({ commit }, value) {
    commit('updateReservationHour', value)
  },
  updateReservationMinute({ commit }, value) {
    commit('updateReservationMinute', padZero(value))
  },
  /* step2 */
  updateReservationTable({ commit }, value) {
    commit('updateReservationTable', value)
  },
  updateDuplicatedIds({ commit }, value) {
    commit('updateDuplicatedIds', value)
  },
  /* step3 */
  updateCustomer({ commit }, value) {
    commit('updateCustomer', value)
  },
  initReservationCourse({ commit }, value) {
    commit('updateReservationCourse', value)
  },
  updateReservationSettlementPoint({ commit }, value) {
    commit('updateReservationSettlement', value)
  },
  /** 予約登録/更新確認ＡＰI */
  async registConfirm(store) {
    const { commit, rootState, rootGetters, state } = store

    if (state.busy) return null

    commit('busy', true)
    const { resetTime, utilizationTime, courses } = state.reservation

    const { status, message } = await POST('/api/web/reservation/regist/confirm', {
      storeId: rootState.stores.current.store.id,
      ...state,
      reservation: {
        ...state.reservation,
        resetTime: resetTime !== null ? resetTime : rootState.stores.current.store.time.reset || 0,
        utilizationTime: utilizationTime || rootGetters['reserves/edit/defaultUtilizationTime'],
        tags: state.reservation.tags.map(tag => tag.id).join(','),
        courses: courses.map(v => pick(v, ['id', 'count'])),
      },
    })

    if (status !== 200) {
      commit('app/error', { status, message }, { root: true })
    }

    commit('busy', false)
    return status === 200
  },
  /** 予約登録/更新ＡＰI */
  async registExecute(store) {
    const { commit, dispatch, rootState, rootGetters, state } = store

    if (state.busy) return null

    commit('busy', true)
    await dispatch('deleteCourseWithNoId')

    const { resetTime, utilizationTime, courses } = state.reservation

    const { status, message, data } = await POST('/api/web/reservation/regist/execute', {
      storeId: rootState.stores.current.store.id,
      ...state,
      reservation: {
        ...state.reservation,
        resetTime: resetTime !== null ? resetTime : rootState.stores.current.store.time.reset || 0,
        utilizationTime: utilizationTime || rootGetters['reserves/edit/defaultUtilizationTime'],
        tags: state.reservation.tags.map(tag => tag.id).join(','),
        courses: courses.map(v => pick({ ...v, name: v.name || v.names?.jpn }, ['id', 'count', 'name', 'price'])),
      },
    })

    if (status !== 200) {
      commit('app/error', { status, message }, { root: true })
    }

    if (data && status === 200) {
      dispatch('updateReservationId', data.reservationId)
      commit('updateCustomer', { id: data.customerId })
    }

    commit('busy', false)
    return status === 200
  },

  /**
   * 「コースなし」を確認遷移時に削除する
   */
  deleteCourseWithNoId({ state, dispatch }) {
    const _courseList = cloneDeep(state.reservation.courses)
    const filteredCourseList = _courseList.filter(course => course.id && course.count > 0)
    if (!isEmpty(filteredCourseList)) {
      dispatch('initReservationCourse', filteredCourseList)
    } else {
      dispatch('initReservationCourse', [])
    }
  },
}

export default {
  namespaced: true,
  state: {
    ...baseState,
  },
  mutations: {
    ...baseMutations,
  },
  getters: {
    ...baseGetters,
  },
  actions: {
    ...baseActions,
  },
}
